import { definePreset } from '@primeuix/themes';
import type { Preset } from '@primeuix/themes/types';
import Lara from '@primeuix/themes/lara';

// Palette Casâbito — parità con index.html (terracotta/crema/marrone caldo).
// Terracotta (#C0533E, ORA) come primary; i nomi palette_* restano per stabilità.
const psychologyLivingPaletteNavy = {
  palette_50: '#FAECE7',
  palette_100: '#F5E6DC',
  palette_200: '#E8C4B8',
  palette_300: '#D89E8C',
  palette_400: '#CD7460',
  palette_500: '#C0533E',
  palette_600: '#A8412E',
  palette_700: '#8A3424',
  palette_800: '#6E281B',
  palette_900: '#531D14',
  palette_950: '#3A140D',
};

// Tinte chiare terracotta per highlight/accenti soft.
const psychologyLivingPaletteNavyTint = {
  palette_50: '#FAECE7',
  palette_100: '#F5E6DC',
  palette_950: '#D89E8C',
};

// Inchiostro testo (marrone quasi-nero, TEXT).
const psychologyLivingPaletteInk = {
  palette_950: '#1F1A14',
};

const psychologyLivingPaletteLightColor = {
  palette_50: '#ffffff',
  palette_100: '#FAF5EA',
};

// Superfici & neutri caldi (CREAM / SAND / NAV / TEXT / MUTED).
const psychologyLivingSurface: Record<number, string> = {
  0: '#ffffff',
  50: '#FAF5EA',
  100: '#F1E6D2',
  200: '#D8C8AB',
  300: '#C9B596',
  400: '#A89B85',
  500: '#8A7A66',
  600: '#5C4F3F',
  700: '#3D3024',
  800: '#2A211A',
  900: '#1F1A14',
  950: '#14100B',
};

const shadowXs: string = '0 1px 2px rgba(31,26,20,0.05)';
const shadowSm: string = '0 1px 3px rgba(31,26,20,0.07), 0 1px 2px rgba(31,26,20,0.05)';
const shadowMd: string = '0 4px 16px rgba(31,26,20,0.09)';
const shadowLg: string = '0 20px 56px rgba(20,16,11,0.26)';

const radiusSm: string = '8px';
const radiusMd: string = '10px';
const radiusLg: string = '14px';
const radiusFull: string = '9999px';

const primaryNavy: string = psychologyLivingPaletteNavy.palette_600; // #013366
const primaryNavyHover: string = psychologyLivingPaletteNavy.palette_700; // #002A5C
const primaryNavyActive: string = psychologyLivingPaletteNavy.palette_800; // #001E45

// Gradiente navy del bottone primario (coerente con l'hero della login).
const primaryButtonGradient: string = `linear-gradient(180deg, ${psychologyLivingPaletteNavy.palette_500} 0%, ${primaryNavy} 100%)`;
const primaryButtonGradientHover: string = `linear-gradient(180deg, ${primaryNavy} 0%, ${primaryNavyHover} 100%)`;
const primaryButtonGradientActive: string = `linear-gradient(180deg, ${primaryNavyHover} 0%, ${primaryNavyActive} 100%)`;

const dangerColor: string = '#C0392B'; // --danger
const successColor: string = '#167A41'; // --green-700

export const psychologyLivingPreset: Preset = definePreset(Lara, {
  semantic: {
    primary: {
      50: psychologyLivingPaletteNavy.palette_50,
      100: psychologyLivingPaletteNavy.palette_100,
      200: psychologyLivingPaletteNavy.palette_200,
      300: psychologyLivingPaletteNavy.palette_300,
      400: psychologyLivingPaletteNavy.palette_400,
      500: psychologyLivingPaletteNavy.palette_500,
      600: psychologyLivingPaletteNavy.palette_600,
      700: psychologyLivingPaletteNavy.palette_700,
      800: psychologyLivingPaletteNavy.palette_800,
      900: psychologyLivingPaletteNavy.palette_900,
      950: psychologyLivingPaletteNavy.palette_950,
    },
    transitionDuration: '0.15s',
    focusRing: {
      width: '0',
      style: 'none',
      color: 'transparent',
      offset: '0',
      shadow: '0 0 0 3px rgba(192, 83, 62, 0.18)',
    },
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      borderRadius: radiusSm,
      transitionDuration: '0.15s',
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: '0 0 0 3px rgba(192, 83, 62, 0.18)',
      },
      sm: { fontSize: '0.8125rem', paddingX: '0.625rem', paddingY: '0.375rem' },
      lg: { fontSize: '1rem', paddingX: '0.875rem', paddingY: '0.625rem' },
    },
    content: { borderRadius: radiusSm },
    colorScheme: {
      light: {
        surface: psychologyLivingSurface,
        primary: {
          color: primaryNavy,
          hoverColor: primaryNavyHover,
          activeColor: primaryNavyActive,
          contrastColor: psychologyLivingPaletteLightColor.palette_50,
        },
        highlight: {
          background: psychologyLivingPaletteNavyTint.palette_50,
          focusBackground: psychologyLivingPaletteNavyTint.palette_100,
          color: psychologyLivingPaletteInk.palette_950,
          focusColor: psychologyLivingPaletteInk.palette_950,
        },
        text: {
          color: psychologyLivingPaletteInk.palette_950,
          hoverColor: psychologyLivingPaletteInk.palette_950,
          mutedColor: psychologyLivingSurface[500],
          hoverMutedColor: psychologyLivingSurface[700],
        },
        content: {
          background: psychologyLivingPaletteLightColor.palette_50,
          hoverBackground: psychologyLivingSurface[100],
          borderColor: psychologyLivingSurface[200],
          color: psychologyLivingSurface[700],
          hoverColor: psychologyLivingSurface[700],
        },
        formField: {
          background: psychologyLivingPaletteLightColor.palette_50,
          disabledBackground: psychologyLivingSurface[100],
          filledBackground: psychologyLivingSurface[50],
          borderColor: psychologyLivingSurface[300],
          hoverBorderColor: psychologyLivingSurface[500],
          focusBorderColor: psychologyLivingPaletteNavy.palette_300, // terracotta chiaro — bordo focus soft (no look "errore"), si fonde con l'alone
          invalidBorderColor: dangerColor,
          color: psychologyLivingSurface[700],
          disabledColor: psychologyLivingSurface[500],
          placeholderColor: psychologyLivingSurface[500],
          floatLabelColor: psychologyLivingSurface[500],
          iconColor: psychologyLivingSurface[500],
          shadow: shadowXs,
        },
      },
    },
  },
  components: {
    button: {
      root: {
        borderRadius: radiusSm,
        roundedBorderRadius: radiusFull,
        paddingX: '1rem',
        paddingY: '0.5rem',
        iconOnlyWidth: '2.25rem',
        gap: '0.5rem',
        transitionDuration: '0.15s',
        raisedShadow: shadowSm,
        label: { fontWeight: '600' },
        sm: {
          fontSize: '0.8125rem',
          paddingX: '0.75rem',
          paddingY: '0.375rem',
          iconOnlyWidth: '2rem',
        },
        lg: {
          fontSize: '1rem',
          paddingX: '1.25rem',
          paddingY: '0.625rem',
          iconOnlyWidth: '2.75rem',
        },
        secondary: {
          background: psychologyLivingSurface[100],
          hoverBackground: psychologyLivingSurface[200],
          activeBackground: psychologyLivingSurface[300],
          borderColor: psychologyLivingSurface[200],
          hoverBorderColor: psychologyLivingSurface[300],
          activeBorderColor: psychologyLivingSurface[500],
          color: psychologyLivingSurface[700],
          hoverColor: psychologyLivingSurface[700],
          activeColor: psychologyLivingSurface[700],
          focusRing: { color: psychologyLivingSurface[700], shadow: 'none' },
        },
        contrast: {
          background: psychologyLivingSurface[700],
          hoverBackground: psychologyLivingSurface[600],
          activeBackground: psychologyLivingSurface[500],
          borderColor: psychologyLivingSurface[700],
          hoverBorderColor: psychologyLivingSurface[600],
          activeBorderColor: psychologyLivingSurface[500],
          color: '#ffffff',
          hoverColor: '#ffffff',
          activeColor: '#ffffff',
        },
      },
      outlined: {
        secondary: {
          hoverBackground: psychologyLivingSurface[100],
          activeBackground: psychologyLivingSurface[200],
          borderColor: psychologyLivingSurface[300],
          color: psychologyLivingSurface[700],
        },
      },
      text: {
        secondary: {
          hoverBackground: psychologyLivingSurface[100],
          activeBackground: psychologyLivingSurface[200],
          color: psychologyLivingSurface[700],
        },
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: primaryButtonGradient,
              hoverBackground: primaryButtonGradientHover,
              activeBackground: primaryButtonGradientActive,
              borderColor: primaryNavy,
              hoverBorderColor: primaryNavyHover,
              activeBorderColor: primaryNavyActive,
            },
          },
        },
      },
    },

    card: {
      root: {
        background: '#ffffff',
        color: psychologyLivingSurface[700],
        borderRadius: radiusLg,
        shadow: shadowMd,
      },
      body: { padding: '1.5rem', gap: '1rem' },
      caption: { gap: '0.5rem' },
      title: { fontSize: '1rem', fontWeight: '600' },
      subtitle: { color: psychologyLivingSurface[500] },
    },

    dialog: {
      root: {
        background: '#ffffff',
        borderColor: psychologyLivingSurface[200],
        color: psychologyLivingSurface[700],
        borderRadius: radiusLg,
        shadow: shadowLg,
      },
      header: { padding: '1.25rem 1.5rem', gap: '0.5rem' },
      title: { fontSize: '1.125rem', fontWeight: '700' },
      content: { padding: '0 1.5rem 1.5rem' },
      footer: { padding: '1rem 1.5rem 1.25rem', gap: '0.75rem' },
    },

    inputtext: {
      root: {
        borderRadius: radiusSm,
        paddingX: '0.75rem',
        paddingY: '0.5rem',
        transitionDuration: '0.15s',
        sm: { fontSize: '0.8125rem', paddingX: '0.625rem', paddingY: '0.375rem' },
        lg: { fontSize: '1rem', paddingX: '0.875rem', paddingY: '0.625rem' },
      },
    },

    inputnumber: { root: { transitionDuration: '0.15s' } },
    textarea: { root: { borderRadius: radiusSm } },
    // Select (singolo e multiplo): pannello largo quanto il campo e opzioni lunghe a capo.
    // PrimeNG di default dà al pannello solo min-width = campo e lo lascia crescere col testo
    // più lungo (sborda, testo troncato con white-space: nowrap). Non esiste un token per
    // questo, quindi `css` del preset: vale per tutti i p-select dell'app.
    select: {
      root: { borderRadius: radiusSm },
      css: () => `
        .p-select .p-overlay { width: 100%; max-width: 100%; }
        .p-select .p-select-overlay { width: 100%; }
        .p-select-option { white-space: normal; overflow-wrap: anywhere; }
        .p-select-label { white-space: normal; overflow-wrap: anywhere; }
      `,
    },
    multiselect: { root: { borderRadius: radiusSm } },
    autocomplete: { root: { borderRadius: radiusSm } },
    datepicker: { root: { transitionDuration: '0.15s' } },
    treeselect: { root: { borderRadius: radiusSm } },
    cascadeselect: { root: { borderRadius: radiusSm } },

    selectbutton: {
      root: {
        borderRadius: radiusSm,
        invalidBorderColor: dangerColor,
      },
    },

    tag: {
      root: {
        fontSize: '0.625rem',
        fontWeight: '600',
        padding: '0.25rem 0.5rem',
        gap: '0.25rem',
        borderRadius: radiusSm,
        roundedBorderRadius: radiusFull,
      },
      primary: { background: psychologyLivingPaletteNavy.palette_50, color: primaryNavy },
      secondary: { background: psychologyLivingSurface[100], color: psychologyLivingSurface[700] },
      success: { background: '#EBF9F1', color: successColor },
      info: { background: '#EEF3FA', color: '#013366' },
      warn: { background: '#FEF3D0', color: '#946A0A' },
      danger: { background: '#FBE8E6', color: dangerColor },
      contrast: { background: psychologyLivingSurface[700], color: '#ffffff' },
    },

    badge: {
      root: {
        borderRadius: radiusFull,
        fontSize: '0.625rem',
        fontWeight: '700',
      },
    },

    accordion: {
      root: { transitionDuration: '0.15s' },
      panel: { borderWidth: '1px', borderColor: psychologyLivingSurface[200] },
      header: {
        padding: '1rem 1.25rem',
        fontWeight: '600',
        // Nessun borderRadius qui: il token "panel" non supporta border-radius (vedi
        // AccordionTokenSections.Panel), quindi l'arrotondamento del riquadro esterno è
        // demandato al CSS in admin.html (:host ::ng-deep .p-accordionpanel, overflow:
        // hidden) — un radius qui creerebbe angoli arrotondati anche dove header e content
        // si toccano, sfasati rispetto al bordo quadrato del panel.
        borderRadius: '0',
        borderWidth: '0',
        borderColor: 'transparent',
        background: '#ffffff',
        hoverBackground: psychologyLivingSurface[100],
        activeBackground: '#ffffff',
        activeHoverBackground: psychologyLivingSurface[50],
        color: psychologyLivingSurface[700],
        hoverColor: primaryNavy,
        activeColor: psychologyLivingSurface[700],
        activeHoverColor: primaryNavy,
      },
      content: {
        borderWidth: '0',
        borderColor: 'transparent',
        background: '#ffffff',
        color: psychologyLivingSurface[700],
        padding: '0 1.25rem 1.25rem',
      },
    },

    steps: {
      root: { transitionDuration: '0.15s' },
      separator: { background: psychologyLivingSurface[200] },
      itemLink: { borderRadius: radiusSm, gap: '0.5rem' },
      itemLabel: {
        color: psychologyLivingSurface[500],
        activeColor: psychologyLivingSurface[700],
        fontWeight: '600',
      },
      itemNumber: {
        background: psychologyLivingSurface[100],
        activeBackground: primaryNavy,
        borderColor: psychologyLivingSurface[200],
        activeBorderColor: primaryNavy,
        color: psychologyLivingSurface[500],
        activeColor: '#ffffff',
        size: '2rem',
        fontSize: '0.875rem',
        fontWeight: '700',
        borderRadius: radiusFull,
        shadow: 'none',
      },
    },

    stepper: {
      root: { transitionDuration: '0.15s' },
      separator: { background: psychologyLivingSurface[200], activeBackground: primaryNavy },
      steppanel: { background: '#ffffff', color: psychologyLivingSurface[700] },
    },

    breadcrumb: {
      root: {
        padding: '0',
        background: 'transparent',
        gap: '0.5rem',
        transitionDuration: '0.15s',
      },
      item: {
        color: psychologyLivingSurface[500],
        hoverColor: primaryNavy,
        icon: { color: psychologyLivingSurface[500], hoverColor: primaryNavy },
      },
      separator: { color: psychologyLivingSurface[300] },
    },

    menu: {
      root: {
        background: psychologyLivingSurface[50],
        borderColor: psychologyLivingSurface[200],
        color: psychologyLivingSurface[700],
        borderRadius: radiusMd,
        shadow: shadowSm,
        transitionDuration: '0.15s',
      },
      list: { padding: '0.5rem', gap: '0.125rem' },
      item: {
        focusBackground: psychologyLivingSurface[100],
        color: psychologyLivingSurface[700],
        focusColor: primaryNavy,
        padding: '0.625rem 0.875rem',
        borderRadius: radiusSm,
        gap: '0.5rem',
        icon: { color: psychologyLivingSurface[500], focusColor: primaryNavy },
      },
      submenuLabel: {
        padding: '0.5rem 0.875rem',
        fontWeight: '600',
        background: 'transparent',
        color: psychologyLivingSurface[500],
      },
      separator: { borderColor: psychologyLivingSurface[200] },
    },

    panelmenu: {
      root: { gap: '0.25rem', transitionDuration: '0.15s' },
      panel: {
        background: 'transparent',
        borderColor: 'transparent',
        borderWidth: '0',
        color: psychologyLivingSurface[700],
        padding: '0',
        borderRadius: radiusMd,
      },
      item: {
        focusBackground: psychologyLivingSurface[100],
        color: psychologyLivingSurface[700],
        focusColor: primaryNavy,
        padding: '0.75rem 0.875rem',
        borderRadius: radiusSm,
        gap: '0.5rem',
        icon: { color: psychologyLivingSurface[500], focusColor: primaryNavy },
      },
      submenuIcon: { color: psychologyLivingSurface[500], focusColor: primaryNavy },
    },

    tabs: {
      root: { transitionDuration: '0.15s' },
      tablist: {
        background: psychologyLivingSurface[50],
        borderColor: psychologyLivingSurface[200],
      },
      tab: {
        background: 'transparent',
        hoverBackground: 'transparent',
        activeBackground: 'transparent',
        borderColor: 'transparent',
        activeBorderColor: primaryNavy,
        color: psychologyLivingSurface[500],
        hoverColor: psychologyLivingSurface[700],
        activeColor: primaryNavy,
        padding: '0.75rem 1rem',
        fontWeight: '600',
        gap: '0.5rem',
      },
      tabpanel: {
        background: 'transparent',
        color: psychologyLivingSurface[700],
        padding: '1rem 0',
      },
    },

    tabmenu: {
      root: { transitionDuration: '0.15s' },
      tablist: { borderColor: psychologyLivingSurface[200] },
      item: {
        background: 'transparent',
        hoverBackground: 'transparent',
        activeBackground: 'transparent',
        color: psychologyLivingSurface[500],
        hoverColor: psychologyLivingSurface[700],
        activeColor: primaryNavy,
        borderColor: 'transparent',
        activeBorderColor: primaryNavy,
        padding: '0.75rem 1rem',
        fontWeight: '600',
        gap: '0.5rem',
      },
    },

    datatable: {
      root: {
        transitionDuration: '0.15s',
        borderColor: psychologyLivingSurface[200],
      },
      header: {
        background: psychologyLivingSurface[50],
        borderColor: psychologyLivingSurface[200],
        color: psychologyLivingSurface[700],
        borderWidth: '0 0 1px 0',
        padding: '0.75rem 1rem',
      },
      headerCell: {
        background: psychologyLivingSurface[50],
        hoverBackground: psychologyLivingSurface[100],
        selectedBackground: psychologyLivingPaletteNavy.palette_50,
        borderColor: psychologyLivingSurface[200],
        color: psychologyLivingSurface[700],
        hoverColor: psychologyLivingSurface[700],
        selectedColor: primaryNavy,
        gap: '0.5rem',
        padding: '0.75rem 1rem',
      },
      columnTitle: { fontWeight: '600' },
      row: {
        background: '#ffffff',
        hoverBackground: psychologyLivingSurface[100],
        selectedBackground: psychologyLivingPaletteNavy.palette_50,
        color: psychologyLivingSurface[700],
        hoverColor: psychologyLivingSurface[700],
        selectedColor: primaryNavy,
        stripedBackground: psychologyLivingSurface[50],
      },
      bodyCell: {
        borderColor: psychologyLivingSurface[200],
        padding: '0.75rem 1rem',
        selectedBorderColor: primaryNavy,
      },
      footerCell: {
        background: psychologyLivingSurface[50],
        borderColor: psychologyLivingSurface[200],
        color: psychologyLivingSurface[700],
        padding: '0.75rem 1rem',
      },
      columnFooter: { fontWeight: '600' },
      footer: {
        background: psychologyLivingSurface[50],
        borderColor: psychologyLivingSurface[200],
        color: psychologyLivingSurface[700],
        borderWidth: '1px 0 0 0',
        padding: '0.75rem 1rem',
      },
      paginatorTop: { borderColor: psychologyLivingSurface[200], borderWidth: '0 0 1px 0' },
      paginatorBottom: { borderColor: psychologyLivingSurface[200], borderWidth: '1px 0 0 0' },
    },

    toggleswitch: {
      root: {
        width: '2.5rem',
        height: '1.375rem',
        borderRadius: radiusFull,
        gap: '0.25rem',
        transitionDuration: '0.15s',
        slideDuration: '0.15s',
        borderWidth: '1px',
        background: psychologyLivingSurface[300],
        disabledBackground: psychologyLivingSurface[200],
        hoverBackground: psychologyLivingSurface[500],
        checkedBackground: primaryNavy,
        checkedHoverBackground: primaryNavyHover,
        borderColor: 'transparent',
        hoverBorderColor: 'transparent',
        checkedBorderColor: 'transparent',
        checkedHoverBorderColor: 'transparent',
        invalidBorderColor: dangerColor,
      },
      handle: {
        borderRadius: radiusFull,
        size: '1.125rem',
        background: '#ffffff',
        disabledBackground: psychologyLivingSurface[100],
        hoverBackground: '#ffffff',
        checkedBackground: '#ffffff',
        checkedHoverBackground: '#ffffff',
        color: psychologyLivingSurface[700],
        hoverColor: psychologyLivingSurface[700],
        checkedColor: primaryNavy,
        checkedHoverColor: primaryNavy,
      },
    },

    checkbox: {
      root: {
        borderRadius: '4px',
        width: '1.125rem',
        height: '1.125rem',
        background: '#ffffff',
        checkedBackground: primaryNavy,
        checkedHoverBackground: primaryNavyHover,
        borderColor: psychologyLivingSurface[300],
        hoverBorderColor: psychologyLivingSurface[500],
        checkedBorderColor: primaryNavy,
        checkedHoverBorderColor: primaryNavyHover,
      },
      icon: { color: '#ffffff', checkedColor: '#ffffff', checkedHoverColor: '#ffffff' },
    },

    radiobutton: {
      root: {
        width: '1.125rem',
        height: '1.125rem',
        background: '#ffffff',
        checkedBackground: '#ffffff',
        borderColor: psychologyLivingSurface[300],
        hoverBorderColor: psychologyLivingSurface[500],
        checkedBorderColor: primaryNavy,
        checkedHoverBorderColor: primaryNavyHover,
      },
      icon: {
        checkedColor: primaryNavy,
        checkedHoverColor: primaryNavyHover,
      },
    },

    fileupload: {
      root: {
        background: '#ffffff',
        borderColor: psychologyLivingSurface[300],
        color: psychologyLivingSurface[700],
        borderRadius: radiusMd,
      },
      header: {
        background: psychologyLivingSurface[50],
        padding: '1rem 1.25rem',
        borderColor: psychologyLivingSurface[200],
        gap: '0.5rem',
      },
      content: { padding: '1.25rem', highlightBorderColor: primaryNavy },
      file: { padding: '0.75rem', borderColor: psychologyLivingSurface[200] },
    },

    progressbar: {
      root: {
        background: psychologyLivingSurface[200],
        borderRadius: radiusFull,
        height: '0.5rem',
      },
      value: { background: primaryNavy },
      label: { color: '#ffffff', fontSize: '0.6875rem', fontWeight: '600' },
    },

    progressspinner: {
      root: {
        // Colore unico della piattaforma (niente ciclo multicolore).
        colorOne: primaryNavy,
        colorTwo: primaryNavy,
        colorThree: primaryNavy,
        colorFour: primaryNavy,
      },
    },

    avatar: {
      root: {
        width: '2rem',
        height: '2rem',
        fontSize: '0.875rem',
        borderRadius: radiusFull,
        background: psychologyLivingSurface[100],
        color: psychologyLivingSurface[700],
      },
      group: { borderColor: '#ffffff', offset: '-0.75rem' },
      lg: { width: '2.5rem', height: '2.5rem', fontSize: '1rem' },
      xl: { width: '3.5rem', height: '3.5rem', fontSize: '1.25rem' },
    },

    divider: {
      root: { borderColor: psychologyLivingSurface[200] },
      content: { background: '#ffffff', color: psychologyLivingSurface[500] },
    },

    chip: {
      root: {
        borderRadius: radiusFull,
        paddingX: '0.625rem',
        paddingY: '0.25rem',
        gap: '0.375rem',
        background: psychologyLivingSurface[100],
        color: psychologyLivingSurface[700],
      },
      icon: { color: psychologyLivingSurface[500] },
      removeIcon: { color: psychologyLivingSurface[500] },
    },

    popover: {
      root: {
        background: '#ffffff',
        borderColor: psychologyLivingSurface[200],
        color: psychologyLivingSurface[700],
        borderRadius: radiusMd,
        shadow: shadowSm,
      },
      content: { padding: '1rem' },
    },

    tooltip: {
      root: {
        background: psychologyLivingSurface[700],
        color: '#ffffff',
        borderRadius: radiusSm,
        padding: '0.375rem 0.625rem',
      },
    },

    drawer: {
      root: {
        background: '#ffffff',
        borderColor: psychologyLivingSurface[200],
        color: psychologyLivingSurface[700],
      },
      header: { padding: '1rem 1.25rem' },
      title: { fontSize: '1.125rem', fontWeight: '700' },
      content: { padding: '1rem 1.25rem' },
      footer: { padding: '1rem 1.25rem' },
    },

    skeleton: {
      root: {
        borderRadius: radiusSm,
        background: psychologyLivingSurface[100],
        animationBackground: psychologyLivingSurface[200],
      },
    },
  },
});
